import type { Question, SvarMulighed } from '@/constants/questions';

interface QuestionCardProps {
  question: Question;
  currentIndex: number;
  totalCount: number;
  selectedSvar: SvarMulighed | undefined;
  onSvar: (svar: SvarMulighed) => void;
}

const SVAR_LABELS: Record<SvarMulighed, { label: string; description: string }> = {
  ingenting: {
    label: 'Ingenting',
    description: 'Vi har ikke fokus på dette',
  },
  noget: {
    label: 'Noget',
    description: 'Vi overvejer det / har gjort lidt',
  },
  enStorDel: {
    label: 'En stor del',
    description: 'Vi prioriterer dette aktivt',
  },
};

const SVAR_OPTIONS: SvarMulighed[] = ['ingenting', 'noget', 'enStorDel'];

export function QuestionCard({
  question,
  currentIndex,
  totalCount,
  selectedSvar,
  onSvar,
}: QuestionCardProps) {
  return (
    <div className="space-y-6">
      {/* Fremdriftsindikator */}
      <div aria-label={`Spørgsmål ${currentIndex + 1} af ${totalCount}`}>
        <div className="flex justify-between text-sm text-gray-400 mb-2">
          <span>Spørgsmål {currentIndex + 1} af {totalCount}</span>
        </div>
        <div className="h-1 bg-gray-100 rounded-full overflow-hidden" role="progressbar"
          aria-valuenow={currentIndex + 1} aria-valuemin={1} aria-valuemax={totalCount}>
          <div
            className="h-full bg-brand rounded-full transition-all duration-300"
            style={{ width: `${((currentIndex + 1) / totalCount) * 100}%` }}
          />
        </div>
      </div>

      {/* Spørgsmål */}
      <div>
        <h2 className="text-2xl font-serif text-gray-900 mb-2">{question.title}</h2>
        <p className="text-gray-600">{question.description}</p>
      </div>

      {/* Svarmuligheder */}
      <fieldset>
        <legend className="sr-only">Vælg svar for: {question.title}</legend>
        <div className="space-y-3">
          {SVAR_OPTIONS.map((svar) => {
            const { label, description } = SVAR_LABELS[svar];
            const isSelected = selectedSvar === svar;
            return (
              <button
                key={svar}
                type="button"
                onClick={() => onSvar(svar)}
                aria-pressed={isSelected}
                className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all duration-150
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2
                  ${isSelected
                    ? 'border-brand bg-brand/5 text-gray-900'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-brand/40 hover:bg-gray-50'
                  }`}
              >
                <span className="font-medium block">{label}</span>
                <span className="text-sm text-gray-500 mt-0.5 block">{description}</span>
              </button>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
}
